"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Info, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button"; // Added Button import for the info icon trigger

// Flatpickr imports
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Instance } from "flatpickr";
import { getFlatpickrLocale } from "@/utils/flatpickrLocale";

interface BirthDateInputProps {
  birthDate: Date | undefined;
  setBirthDate: (date: Date | undefined) => void;
  error: string;
  isLoading: boolean;
  groupRef: React.RefObject<HTMLDivElement>;
  petName: string;
  petType: "cat" | "dog" | null;
}

const BirthDateInput: React.FC<BirthDateInputProps> = ({
  birthDate,
  setBirthDate,
  error,
  isLoading,
  groupRef,
  petName,
  petType,
}) => {
  const { t, i18n } = useTranslation();
  const fpInstanceRef = React.useRef<Instance | null>(null);

  const flatpickrOptions = React.useMemo(() => {
    const today = new Date();
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - 25);

      return {
        dateFormat: "d.m.Y",
        altInput: true,
        altFormat: "d.m.Y",
        maxDate: today,
        minDate: minDate,
        locale: getFlatpickrLocale(i18n.language),
        disableMobile: true,
        allowInput: true,
        clickOpens: false
      };
    }, [i18n.language]);

  const handleFlatpickrChange = (selectedDates: Date[], dateStr: string, instance: Instance) => {
    if (selectedDates.length > 0) {
      setBirthDate(selectedDates[0]);
    } else {
      setBirthDate(undefined);
    }
  };

  return (
    <div className="space-y-2" ref={groupRef}>
      <div className="flex items-center gap-2">
        <Label htmlFor="birth-date">
          {petName ? t("petDetails.birthDate", { petName: petName}) : t("petDetails.birthDateUndefined")}
        </Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4 text-gray-500" />
              <span className="sr-only">{t("common.moreInfo")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("pet-info-infobutton-title-pet-birthdate-explanation")}</DialogTitle>
              <DialogDescription>
                {t("pet-info-pet-birthdate-explanation")}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <div className="relative">
        <Flatpickr
          key={i18n.language}
          value={birthDate}
          options={flatpickrOptions}
          onChange={handleFlatpickrChange}
          onReady={(_, __, instance) => {
            fpInstanceRef.current = instance;
          }}
          render={(fpProps, ref) => {
            const { defaultValue } = fpProps as { defaultValue?: string };
            return (
              <Input
                id="birth-date"
                placeholder={t("common.datePlaceholder")}
                disabled={isLoading}
                className={cn(
                  "w-full pr-10",
                  error && "border-destructive focus-visible:ring-destructive"
                )}
                defaultValue={defaultValue}
                ref={ref as React.Ref<HTMLInputElement>}
              />
            );
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
          onClick={() => fpInstanceRef.current?.open()}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="sr-only">
            {t("common.openCalendar", { defaultValue: "Kalender öffnen" })}
          </span>
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default BirthDateInput;
